<template>
  <div>
    <div class="ibox" v-show="haveLoadChecks">
      <div class="ibox-title">
        <i class="fa fa-exclamation-triangle"></i> Weighbridge Issues
      </div>
      <div class="ibox-content">

        <ul class="list-detail list-detail-load-breakdown">
          <load-check-list-item v-for="load in loadCheckLoads" :key="load.id" :load="load"></load-check-list-item>
        </ul>
        <div class="text-center text-muted" v-show="haveNoLoadChecks">
          <strong>No weighbridge issues.</strong>
        </div>

      </div>
      <lazy-loading v-show="haveLoadChecks" :reached-bottom="getLoadChecksForNextPage" :filters="filters" ref="loadCheckLazyLoading">
        <strong slot="finished" class="text-muted">There are no more weighbridge issues.</strong>
      </lazy-loading>
    </div>

    <div class="ibox">
      <a class="ibox-results-collapse visible-xs" role="button" data-toggle="collapse" href=".ibox-results-filters-collapse-box" aria-expanded="false" aria-controls="ibox-results-filters-collapse-box">
        Show Filters <i class="fa fa-bars"></i>
      </a>
      <div class="ibox-results-filters-collapse-box collapse in">
        <div class="ibox-results-filters">
          <div class="row">
            <div class="col-sm-6 col-lg-4">
              <div class="form-group">
                <label class="control-label">Keywords</label>
                <input type="text" class="form-control" placeholder="" @input="updateKeywords" />
              </div>
            </div>
            <div class="col-sm-6 col-lg-4">
              <div class="form-group">
                <label class="control-label">Rego</label>
                <input type="text" class="form-control" placeholder="" @input="updateRegistration" />
              </div>
            </div>
            <div class="col-sm-6 col-lg-4">
              <div class="form-group">
                <label class="control-label">Commodity</label>
                <selectize :options="commodityOptions" v-model="commodity" name="commodity"></selectize>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 col-lg-4" v-if="$user.isAdminOrCoordinator">
              <div class="form-group">
                <label class="control-label">User</label>
                <selectize :options="initialUserOptions" v-model="carrier_id" name="user" :fetch-results="fetchUserResults"></selectize>
              </div>
            </div>
            <div class="col-sm-6 col-lg-4">
              <div class="form-group">
                <label class="control-label">Status</label>
                <selectize :options="statusOptions" v-model="status" name="status"></selectize>
              </div>
            </div>
            <div class="col-sm-6 col-lg-4">
              <div class="form-group">
                <label class="control-label">Date Range</label>
                <date-range v-model="dateRange" name="date_range"></date-range>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="ibox-content">
        <ul class="list-detail list-detail-load-breakdown list-detail-load-breakdown-carrier">
          <load-item v-for="load in loads" :key="load.id" :load="load"></load-item>
        </ul>
        <div class="text-center text-muted" v-show="haveNoLoads">
          <strong>There are no loads.</strong>
        </div>
      </div>
      <lazy-loading v-show="haveLoads" :reached-bottom="getLoadsForNextPage" :filters="filters" ref="lazyLoading">
        <strong slot="finished" class="text-muted">There are no more loads.</strong>
      </lazy-loading>
    </div>

    <load-check-modal v-if="haveLoad"></load-check-modal>
  </div>
</template>

<script>
  import { LazyLoading } from 'components/misc'
  import { Selectize, DateRange } from 'components/forms'
  import { debounce, isEqual, cloneDeep } from 'lodash'
  import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
  import { Roles } from 'utils/user'
  import { RESET_LOAD, REMOVE_LOAD } from 'vx/mutations'
  import LoadItem from './LoadItem'
  import LoadCheckListItem from './LoadCheckListItem'
  import LoadCheckModal from './LoadCheckModal'

  export default {
    components: {
      Selectize,
      LazyLoading,
      DateRange,
      LoadItem,
      LoadCheckListItem,
      LoadCheckModal
    },

    data () {
      return {
        initialUserOptions: [],
        keywords: '',
        registration: '',
        commodity: null,
        status: '',
        carrier_id: null,
        dateRange: {
          from: null,
          to: null
        }
      }
    },

    watch: {
      filters: {
        handler (newFilters, oldFilters) {
          if (isEqual(newFilters, oldFilters)) {
            return
          }

          // Reset the lazy loading component so that it will attempt to load
          // more jobs as we scroll again.
          const resetLazyLoading = () => (this.$refs.lazyLoading.$emit('reset'))
          const resetLoadCheckLazyLoading = () => {
            if (this.$refs.loadCheckLazyLoading) {
              this.$refs.loadCheckLazyLoading.$emit('reset')
            }
          }

          this.getLoads(this.filters).then(resetLazyLoading)
          this.getLoadChecks({
            carrier_id: this.$user.isCarrier ? this.$user.id : this.filters.carrier_id,
            issues: true
          }).then(resetLoadCheckLazyLoading)
        },
        deep: true
      }
    },

    created () {
      this[RESET_LOAD]()

      this.getCommodities()
      this.getLoads(this.filters)

      // Fetch the loads that have issues and need checking by either the logged in
      // carrier or by an administrator.
      if (this.$user.isCarrier) {
        this.getLoadChecks({ carrier_id: this.$user.id, issues: true })
      } else {
        this.getLoadChecks({ issues: true })
      }

      // When a load state is updated we may need to remove the load from the
      // weighbridge issues if it no longer has issues.
      this.$bus.$on('loadStateUpdated', this.checkUpdatedLoadInWeighbridgeIssues)

      // If logged in user is an admin then we need to let them filter the loads by
      // a user so they have finer control.
      if (this.$user.isAdminOrCoordinator) {
        const setInitialUserOptions = () => (this.initialUserOptions = cloneDeep(this.userOptionsWithAll))

        this.getUsers({ roles: [Roles.ADMIN, Roles.COORDINATOR, Roles.CARRIER], master: true }).then(setInitialUserOptions)

        if (this.$route.query.user) {
          this.carrier_id = this.$route.query.user
        }
      }
    },

    beforeDestroy () {
      this.$bus.$off('loadStateUpdated', this.checkUpdatedLoadInWeighbridgeIssues)
    },

    computed: {
      ...mapState('load', ['loads']),
      ...mapState('loadCheck', { loadCheckLoads: 'loads' }),
      ...mapGetters('load', ['haveLoad', 'haveLoads', 'haveNoLoads']),
      ...mapGetters('loadCheck', ['haveLoadChecks', 'haveNoLoadChecks']),
      ...mapGetters('user', ['userOptionsWithAll']),
      ...mapGetters('commodity', { commodityOptions: 'commodityOptionsWithAll' }),

      filters () {
        return {
          keywords: this.keywords,
          registration: this.registration,
          status: this.status,
          commodity: this.commodity,
          from: this.dateRange.from,
          to: this.dateRange.to,
          carrier_id: this.carrier_id
        }
      },

      statusOptions () {
        let options = [
          { id: 'all', text: 'All' },
          { id: 'scheduled', text: 'Scheduled' },
          { id: 'in_progress', text: 'In Progress' },
          { id: 'picked_up', text: 'Picked Up' },
          { id: 'delivered', text: 'Delivered' },
          { id: 'confirmed', text: 'Confirmed' },
          { id: 'cancelled', text: 'Cancelled' }
        ]

        if (this.$user.isAdmin) {
          options.push({ id: 'withdrawn', text: 'Withdrawn' })
        }

        return options
      }
    },

    methods: {
      ...mapMutations('load', [RESET_LOAD]),
      ...mapMutations('loadCheck', { removeLoadFromLoadCheck: REMOVE_LOAD }),
      ...mapActions('load', ['getLoads', 'getLoadsForNextPage']),
      ...mapActions('loadCheck', ['getLoadChecks', 'getLoadChecksForNextPage']),
      ...mapActions('user', ['getUsers']),
      ...mapActions('commodity', ['getCommodities']),

      /**
       * Attempts to remove the load from the weighbridge issues section if the status
       * of the loads weighbridge is now pending.
       *
       * @param object load
       *
       * @return void
       */
      checkUpdatedLoadInWeighbridgeIssues (load) {
        if (load.weighbridge === null || load.weighbridge.status === 'resubmit') {
          return
        }

        this.removeLoadFromLoadCheck(load)

        this.$bus.$emit('updateNavigation')
      },

      /**
       * When admin types in the user select box we need to fetch more users
       * from the api since the users are paginated.
       */
      fetchUserResults (name, callback) {
        return this.getUsers({ roles: [Roles.ADMIN, Roles.COORDINATOR, Roles.CARRIER], name, master: true }).then(response => {
          callback(this.userOptionsWithAll)
        })
      },

      /**
       * Debounce updating of keywords so we don't poll API immediately for new loads.
       *
       * @param object event
       *
       * @return Function
       */
      updateKeywords: debounce(function (event) {
        this.keywords = event.target.value
      }, 800),

      /**
       * Debounce updating of keywords so we don't poll API immediately for new loads.
       *
       * @param object event
       *
       * @return Function
       */
      updateRegistration: debounce(function (event) {
        this.registration = event.target.value
      }, 800)
    }
  }
</script>



// WEBPACK FOOTER //
// Loads.vue?0f85911e